/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect } from 'react';

import { useCalendarContext } from 'modules/calendar/Context/CalendarContext';
import { UserDetailsContext } from 'context/userDetailsContext';

import EventTitle from './Components/EventTitle';
import EventCaseName from './Components/EventCaseName';
import EventNotLinkedToCase from './Components/EventNotLinkedToCase';
import EventType from './Components/EventType/EventType';
import EventDate from './Components/EventDate';
import EventTime from './Components/EventTime';
import EventVideoConferenceUrl from './Components/EventVideoConferenceUrl';
import EventLocation from './Components/EventLocation/EventLocation';
import EventAttendees from './Components/EventAttendees/EventAttendees';
import EventDescription from './Components/EventDescription';
import EventAttachments from './Components/EventAttachments';
import EventReminders from './Components/EventReminders';

import { parseReminderData } from 'utils/utils';
import EventPracticeArea from './Components/EventPracticeArea';
import EventRecurring from './Components/EventRecurring';
import EventAllDay from './Components/EventAllDay';
import momentTz from 'moment-timezone';
import { Controller } from 'react-hook-form';
import { InputSwitch } from 'primereact/inputswitch';
import LabelName from 'components/UI/LabelName/LabelName';

function CreateEventForm({ control, resetField, setValue, getValues, watch, errors, trigger, setIsCaseUpdated, caseDetails = '' }) {
  const { isGlobal, editEventModal, eventDetails, setUploadedAttachments, isClientCalendar, showReminder } = useCalendarContext();
  const userContext = useContext(UserDetailsContext);

  const notLinkedToCaseWatcher = watch('not_linked_to_case');
  const whenWatcher = watch('when');
  const startTimeWatcher = watch('meeting_start_time');
  const endTimeWatcher = watch('meeting_end_time');
  const selectedcase = watch('case') || eventDetails?.case;
  const allDay = watch('all_day');
  const firmWideWatcher = watch('firm_wide');

  const setTimezoneWithoutAlteringTime = (dateInput) => {
    const timeZone = userContext?.userDetails?.timezone;
    const local = momentTz.tz(dateInput, timeZone);

    const timeParts = {
      year: local.year(),
      month: local.month(),
      day: local.date(),
      hour: local.hour(),
      minute: local.minute(),
      second: local.second(),
    };

    return new Date(timeParts.year, timeParts.month, timeParts.day, timeParts.hour, timeParts.minute, timeParts.second);
  };

  useEffect(() => {
    if (userContext?.userDetails?.reminder && !editEventModal) {
      setValue('reminder', parseReminderData(userContext?.userDetails?.reminder));
    }
  }, [userContext, editEventModal]);

  useEffect(() => {
    if (editEventModal && eventDetails) {
      setValue('title', eventDetails.title);
      setValue('case', eventDetails?.case);
      setValue('not_linked_to_case', eventDetails?.not_linked_to_case);
      setValue('firm_wide', Boolean(eventDetails?.firm_wide));
      setValue('event_type.event_type', eventDetails?.event_type?.event_type);

      const meetingDate = momentTz.tz(eventDetails.when, userContext?.userDetails?.timezone);
      const meetingStartMoment = momentTz.tz(eventDetails?.meeting_start_time, userContext?.userDetails?.timezone);
      const meetingEndMoment = momentTz.tz(eventDetails?.meeting_end_time, userContext?.userDetails?.timezone);

      // Ensure the date field reflects the user's timezone day without shifting
      setValue('when', setTimezoneWithoutAlteringTime(meetingStartMoment));
      setValue('meeting_start_time', meetingStartMoment.format('YYYY-MM-DDTHH:mm'));
      setValue('meeting_end_time', meetingEndMoment.format('YYYY-MM-DDTHH:mm'));

      // Prefill Till date for all-day events using meeting_end_time's day in user's timezone
      if (eventDetails?.all_day) {
        setValue('till', setTimezoneWithoutAlteringTime(meetingEndMoment));
      }

      setValue('conference_url', eventDetails?.conference_url);

      // to remove the current user from the accessible_list
      setValue('selected_attendees_list', eventDetails?.selected_attendees_list);
      setValue('contacts', eventDetails?.contacts ?? []);
      setValue('firm_users', eventDetails?.firm_users ?? []);

      // console.log(eventDetails)

      setValue('guests', eventDetails?.guests);

      let groups = eventDetails?.groups?.length > 0 ? eventDetails?.groups : [];

      setValue('groups', groups);

      setUploadedAttachments(eventDetails?.attachments);

      setValue('location', eventDetails.location);
      setValue('conference_room_id', eventDetails.conference_room_id || eventDetails.conference_room || '');
      if (eventDetails?.practice_area) {
        setValue('practice_area', eventDetails?.practice_area);
      }
      setValue('description', eventDetails.description);

      // set reminders and custom reminder properties
      if (eventDetails?.reminder?.length > 0) {
        setValue('reminder', parseReminderData(eventDetails.reminder));
      }

      setValue('is_recurring', eventDetails.is_recurring);

      const reccrrenceObject = {
        ...eventDetails?.recurrence_keys,
        recurrence_limit: setTimezoneWithoutAlteringTime(
          momentTz.utc(eventDetails?.recurrence_keys?.recurrence_limit).tz(userContext?.userDetails?.timezone)
        ),
      };

      setValue('recurrence_keys', reccrrenceObject);
      setValue('res_type', eventDetails?.res_type);
      setValue('all_day', eventDetails?.all_day);
    }
  }, [editEventModal, eventDetails]);

  let timezoneBasedTime = new Date().toLocaleString('en-US', {
    timeZone: userContext?.userDetails?.timezone,
  });

  return (
    <form>
      {/* form heading */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="modal-heading F-size16 text-bold">{editEventModal ? 'Event Details' : 'Create Event'}</div>
      </div>
      <EventTitle control={control} errors={errors} watch={watch} />
      {isGlobal && !firmWideWatcher ? (
        <EventCaseName
          control={control}
          errors={errors}
          notLinkedToCaseWatcher={notLinkedToCaseWatcher}
          isClientCalendar={isClientCalendar}
          setIsCaseUpdated={setIsCaseUpdated}
        />
      ) : null}
      {isGlobal && !firmWideWatcher ? (
        <EventNotLinkedToCase
          control={control}
          resetField={resetField}
          selectedcase={selectedcase}
          watch={watch}
          setValue={setValue}
          editEventModal={editEventModal}
        />
      ) : null}
      <EventType
        control={control}
        setValue={setValue}
        resetField={resetField}
        watch={watch}
        practice_area_id={caseDetails?.case_description?.case_practice_area}
      />
      <EventDate
        control={control}
        trigger={trigger}
        errors={errors}
        setValue={setValue}
        timezoneBasedTime={timezoneBasedTime}
        watch={watch}
      />
      <EventAllDay control={control} resetField={resetField} watch={watch} setValue={setValue} editEventModal={editEventModal} />
      {!allDay ? (
        <EventTime
          control={control}
          trigger={trigger}
          errors={errors}
          setValue={setValue}
          timezoneBasedTime={timezoneBasedTime}
          whenWatcher={whenWatcher}
          startTimeWatcher={startTimeWatcher}
          endTimeWatcher={endTimeWatcher}
          getValues={getValues}
          watch={watch}
        />
      ) : null}
      <EventRecurring
        control={control}
        resetField={resetField}
        setValue={setValue}
        trigger={trigger}
        watch={watch}
        isGlobal={isGlobal}
        errors={errors}
        timezoneBasedTime={timezoneBasedTime}
      />
      {(userContext?.access_level === 'firm-admin' || userContext?.userDetails?.adminPrivileges) && isGlobal && (
        <>
          <div className="d-flex align-items-center py-2 F-size14 flex-wrap ">
            <div className="col-md-4 col-12">
              <LabelName required={false} htmlFor="firm_wide">
                Firm-Wide Event
              </LabelName>
            </div>
            <div className="col-md-8 col-12 d-flex align-items-center justify-content-start text-capitalize edit-field">
              <div className="w-100 ">
                <Controller
                  name="firm_wide"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <InputSwitch
                      name={field.name}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.value)}
                      className="input-swich"
                    />
                  )}
                />
              </div>
            </div>
          </div>
          {firmWideWatcher && (
            <div className="d-flex align-items-center mb-2">
              <div className="col-12">
                <div
                  className="d-flex align-items-center p-2 rounded"
                  style={{ backgroundColor: '#FFF3CD', border: '1px solid #FFECB5', color: '#856404' }}
                >
                  <i className="pi pi-exclamation-triangle mr-2" style={{ color: '#FF9800', fontSize: '16px' }}></i>
                  <span className="F-size14">This will be a firm-wide event visible to all employees</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <EventVideoConferenceUrl control={control} watch={watch} />
      <EventLocation control={control} setValue={setValue} watch={watch} />
      {notLinkedToCaseWatcher && <EventPracticeArea control={control} setValue={setValue} watch={watch} />}
      {!firmWideWatcher && (
        <EventAttendees
          control={control}
          getValues={getValues}
          setValue={setValue}
          trigger={trigger}
          selectedcase={selectedcase}
          watch={watch}
        />
      )}
      <EventDescription control={control} watch={watch} />

      {/* {!notLinkedToCaseWatcher && ( */}
      <EventAttachments control={control} setValue={setValue} getValues={getValues} watch={watch} />
      {/* )} */}

      <EventReminders
        control={control}
        setValue={setValue}
        watch={watch}
        isGlobal={isGlobal}
        errors={errors}
        isRequired={showReminder}
        optionText="before event start time"
      />
    </form>
  );
}

export default CreateEventForm;
